import { ElMessage, ElLoading } from 'element-plus'
import request from '@/utils/request'
import store from '@/store'

// 获取ContentArea组件实例的辅助函数
export function getContentAreaInstance() {
  try {
    // 添加防御性检查确保store和state存在
    if (!store) {
      console.error('获取ContentArea实例失败: store未定义')
      return null
    }
    
    const contentAreaInstance = store.state.contentAreaInstance
    if (!contentAreaInstance) {
      console.warn('ContentArea实例未找到，可能组件还未挂载')
      return null
    }
    
    return contentAreaInstance
  } catch (error) {
    console.error('获取ContentArea实例时出错:', error)
    return null
  }
}

export class AITranslationService {
  static async getAnalysis(store) {
    let loading = null
    
    try {
      // 确保有当前文件
      const currentFile = store.state.currentFile
      if (!currentFile) {
        ElMessage.warning('请先选择文件')
        return Promise.resolve() // 返回resolved promise
      }
      
      // 获取原文内容
      const sourceText = store.state.sourceText
      if (!sourceText) {
        ElMessage.warning('文件内容为空')
        return Promise.resolve() // 返回resolved promise
      }
      
      // 移除权限检查，所有用户都可以使用解析功能

      // 获取ContentArea组件实例，检查是否有分段文章
      const contentAreaInstance = getContentAreaInstance()
      if (!contentAreaInstance) {
        ElMessage.error('无法获取文章区域组件，请刷新页面重试')
        return Promise.resolve() // 返回resolved promise
      }
      
      // 检查是否有分段文章
      const passages = contentAreaInstance.passages
      if (!passages || passages.length === 0) {
        ElMessage.error('无法获取文章段落信息，请刷新页面重试')
        return Promise.resolve() // 返回resolved promise
      }
      
      // 获取当前选中的段落
      const currentPassageIndex = contentAreaInstance.currentPassageIndex
      let textToAnalyze = sourceText
      let passageInfo = ''
      
      if (currentPassageIndex !== null && passages.length > 1) {
        // 有分段，分析当前段落
        const currentPassage = passages[currentPassageIndex]
        if (currentPassage && currentPassage.text) {
          textToAnalyze = currentPassage.text
          passageInfo = ` (P${currentPassageIndex + 1})`
        }
      }
      
      // 显示加载状态
      loading = ElLoading.service({
        text: `正在分析文章${passageInfo}...`,
        background: 'rgba(0, 0, 0, 0.7)'
      })
      
      // 调用后端API，使用更长的超时时间
      const response = await request.post('/api/translate/analysis', {
        text: textToAnalyze,
        passageInfo: passageInfo
      }, {
        timeout: 120000  // 2分钟超时，AI分析需要较长时间
      })
      
      // 关闭加载状态
      if (loading) {
        loading.close()
      }
      
      if (response && response.content) {
        // 显示分析结果
        store.commit('setAiDialogTitle', `文章分析${passageInfo}`)
        store.commit('setAiDialogContent', response.content)
        store.commit('setAiDialogVisible', true)
      } else {
        ElMessage.error('分析失败，请稍后重试')
      }
      
    } catch (error) {
      // 关闭加载状态
      if (loading) {
        loading.close()
      }
      
      console.error('获取分析结果失败:', error)
      ElMessage.error('分析失败：' + (error.message || '未知错误'))
    }
  }

  static async getScore(store) {
    let loading = null
    
    try {
      // 确保有当前文件
      const currentFile = store.state.currentFile
      if (!currentFile) {
        ElMessage.warning('请先选择文件')
        return Promise.resolve()
      }
      
      // 获取原文和自译内容
      const sourceText = store.state.sourceText
      const selfTranslation = store.state.selfTranslation
      
      if (!sourceText) {
        ElMessage.warning('原文内容为空')
        return Promise.resolve()
      }
      
      if (!selfTranslation) {
        ElMessage.warning('自译内容为空，请先在自译框中输入译文')
        return Promise.resolve()
      }
      
      // 获取ContentArea组件实例
      const contentAreaInstance = getContentAreaInstance()
      if (!contentAreaInstance) {
        ElMessage.error('无法获取文章区域组件，请刷新页面重试')
        return Promise.resolve()
      }
      
      // 检查是否有分段文章
      const passages = contentAreaInstance.passages
      const currentPassageIndex = contentAreaInstance.currentPassageIndex
      
      let sourceTextToScore = sourceText
      let selfTranslationToScore = selfTranslation
      let passageInfo = ''
      
      if (currentPassageIndex !== null && passages.length > 1) {
        // 有分段，评分当前段落
        const currentPassage = passages[currentPassageIndex]
        if (currentPassage && currentPassage.text) {
          sourceTextToScore = currentPassage.text
          passageInfo = ` (P${currentPassageIndex + 1})`
          
          // 对于分段文章，需要从自译内容中提取对应段落
          // 这里简化处理，使用全部自译内容
        }
      }
      
      // 显示加载状态
      loading = ElLoading.service({
        text: `正在评分${passageInfo}...`,
        background: 'rgba(0, 0, 0, 0.7)'
      })
      
      // 调用后端API，使用更长的超时时间
      const response = await request.post('/api/translate/score', {
        sourceText: sourceTextToScore,
        translatedText: selfTranslationToScore,
        filePath: currentFile.path,
        passageInfo: passageInfo
      }, {
        timeout: 180000  // 3分钟超时，AI评分需要更长时间
      })
      
      // 关闭加载状态
      if (loading) {
        loading.close()
      }
      
      if (response && response.content) {
        // 显示评分结果
        store.commit('setAiDialogTitle', `翻译评分${passageInfo}`)
        store.commit('setAiDialogContent', response.content)
        store.commit('setAiDialogVisible', true)
      } else {
        ElMessage.error('评分失败，请稍后重试')
      }
      
    } catch (error) {
      // 关闭加载状态
      if (loading) {
        loading.close()
      }
      
      console.error('获取评分结果失败:', error)
      ElMessage.error('评分失败：' + (error.message || '未知错误'))
    }
  }
}